import React, { useRef, useEffect, useState, useMemo } from "react"
import { FormControlLabel, Switch, makeStyles, Theme } from "@material-ui/core"
import { colors } from "@citydna/common"
import { useData } from "../common/DataProvider"
import { generateCompletionScoreArcadeExpression } from "../common/completionHelpers"
import { parseEsriFields } from "../feature-form/fieldHelpers"
import { useEsriModules } from "../common/useEsriModules"

/** Scoped @material-ui/core stylesheet */
const useStyles = makeStyles<Theme>(theme => ({
  formControlLabel: {
    "& .MuiTypography-root": {
      ...theme.typography.button,
      textTransform: "none",
      color:
        theme.palette.type === "dark"
          ? theme.palette.grey[50]
          : theme.palette.grey[800],
    },
  },
}))

export const ToggleCompletionRenderer: React.FC = () => {
  /** Grab the active feature layer */
  const {
    activeFeatureLayer: [featureLayer],
  } = useData()

  /** Store original renderer when feature layer loads */
  const originalRendererRef = useRef<__esri.Renderer | undefined>()
  useEffect(() => {
    // @ts-ignore
    originalRendererRef.current = featureLayer?.renderer.clone()
  }, [featureLayer])

  /** Grab the esri modules we need */
  const [SimpleRenderer] = useEsriModules<[typeof __esri.SimpleRenderer]>([
    "esri/renderers/SimpleRenderer",
  ])

  /** Generate completion score renderer */
  const completionScoreRenderer = useMemo(() => {
    if (featureLayer && SimpleRenderer) {
      return new SimpleRenderer({
        symbol: {
          size: 8,
          type: "simple-marker",
          outline: {
            color: "white",
            width: 1,
          },
        } as __esri.SymbolProperties,
        visualVariables: [
          {
            type: "color",
            valueExpressionTitle:
              "Colours the points based on how many fields have been completed",
            // generate an arcade expression that'll compile the
            valueExpression: generateCompletionScoreArcadeExpression(
              parseEsriFields(
                featureLayer.fields.filter(
                  f => f.name.includes("dna_") && !f.alias.includes("switch")
                )
              )
            ),
            stops: [
              { value: -1, color: colors.pop.magenta, label: "Incomplete" },
              { value: 0, color: colors.pop.orange, label: "Needs work" },
              { value: 0.49, color: colors.pop.orange, label: "Needs work" },
              { value: 0.5, color: colors.pop.green, label: "Sufficient" },
            ],
          } as __esri.VisualVariableProperties,
        ],
      })
    }
  }, [featureLayer, SimpleRenderer])

  /** Flip between the renderers when the switch toggles */
  const [useCompletionScore, setUseCompletionScore] = useState(true)
  useEffect(() => {
    if (useCompletionScore && featureLayer && completionScoreRenderer) {
      featureLayer.renderer = completionScoreRenderer
    } else if (featureLayer && originalRendererRef.current) {
      featureLayer.renderer = originalRendererRef.current
    }
  }, [useCompletionScore, completionScoreRenderer, featureLayer])

  /** Get scoped CSS classes */
  const classes = useStyles()

  return (
    <FormControlLabel
      className={classes.formControlLabel}
      control={
        <Switch
          checked={useCompletionScore}
          onChange={e => setUseCompletionScore(e.target.checked)}
        />
      }
      label="Colour incomplete features on map"
    />
  )
}
